import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from 'react';

import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import { ColorModeToggle } from '@/components/common/ColorModeToggle';
import { LanguageToggle } from '@/components/common/LanguageToggle';
import { Logo } from '@/components/common/Logo';
import { NotificationBell } from '@/features/discovery/NotificationBell';
import { useLogout, type Session } from '@/features/auth/useSession';
import { useToast } from '@/components/common/toast-context';
import { layout } from '@/theme';

interface AppHeaderProps {
  session: Session;
}

/**
 * Sticky, translucent, blurred (spec §9.1/§9.3).
 *
 * Search lives here rather than in the rail because it is the one action that
 * makes sense from every screen; the notification bell sits beside it for the
 * same reason.
 */
export function AppHeader({ session }: AppHeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logout = useLogout();
  const { showToast } = useToast();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const searchField = useRef<HTMLInputElement>(null);
  const { pathname, search } = useLocation();
  // Null anywhere but the search page, and — this is the point — the *same*
  // null across every other URL change, so the feed rewriting its query string
  // as filters change cannot wipe a half-typed query out of the field below.
  const activeQuery = pathname === '/search' ? (new URLSearchParams(search).get('q') ?? '') : null;
  const [query, setQuery] = useState(activeQuery ?? '');

  /**
   * The field follows the URL, so arriving at a result set from a link or the
   * back button shows what is actually being searched for rather than whatever
   * was last typed here.
   */
  useEffect(() => {
    setQuery(activeQuery ?? '');
  }, [activeQuery]);

  const closeMenu = (): void => setMenuAnchor(null);

  const handleSignOut = (): void => {
    closeMenu();
    logout.mutate(undefined, {
      onSuccess: () => {
        showToast(t('auth.signedOut'), 'success');
        navigate('/');
      },
      onError: () => showToast(t('auth.signOutFailed'), 'error'),
    });
  };

  return (
    <AppBar position="sticky">
      {/* The toolbar spans the window; its *contents* sit in the same container
          the page below uses, so the logo starts where the nav rail starts and
          the account menu ends where the sidebar ends. */}
      <Toolbar disableGutters sx={{ height: layout.appBarHeight, minHeight: 0 }}>
        <Container
          maxWidth={false}
          sx={{
            maxWidth: layout.pageMaxWidth,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            component={RouterLink}
            to="/"
            aria-label={t('common.homeLink')}
            sx={{ display: 'flex', textDecoration: 'none', color: 'inherit', flexShrink: 0 }}
          >
            <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
              <Logo markOnly />
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <Logo />
            </Box>
          </Box>

          {/* A real search form, so Enter submits it the way a search box is
              expected to and the magnifier is something you can click rather
              than an ornament. */}
          <Box
            component="form"
            role="search"
            onSubmit={(event: FormEvent<HTMLFormElement>) => {
              event.preventDefault();
              const value = query.trim();
              // Nothing typed yet: put the cursor in the field rather than
              // sending the reader to an empty result page.
              if (!value) {
                searchField.current?.focus();
                return;
              }
              navigate(`/search?q=${encodeURIComponent(value)}`);
            }}
            sx={{ flexGrow: 1, maxWidth: 420, display: { xs: 'none', sm: 'block' } }}
          >
            <TextField
              fullWidth
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('discovery.searchPlaceholder')}
              inputRef={searchField}
              inputProps={{ 'aria-label': t('nav.search') }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      type="submit"
                      edge="start"
                      size="small"
                      aria-label={t('nav.search')}
                    >
                      <SearchRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* `ml: auto` rather than a spacer div: the account controls belong
              at the container's right edge, which is where the sidebar ends. */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, ml: 'auto' }}>
            <IconButton
              aria-label={t('nav.search')}
              onClick={() => navigate('/search')}
              sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              size="small"
            >
              <SearchRoundedIcon fontSize="small" />
            </IconButton>

            <LanguageToggle />

            <ColorModeToggle />

            {session.user && <NotificationBell />}

            {session.isLoading ? (
              <Skeleton variant="circular" width={32} height={32} />
            ) : session.user ? (
              <>
                <IconButton
                  onClick={(event: MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget)}
                  aria-label={t('common.accountMenu')}
                  aria-haspopup="menu"
                  aria-expanded={menuAnchor !== null}
                  size="small"
                >
                  <Avatar
                    src={session.user.avatarUrl ?? undefined}
                    alt=""
                    sx={{ width: 32, height: 32, fontSize: '0.875rem' }}
                  >
                    {session.user.displayName.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>

                <Menu
                  anchorEl={menuAnchor}
                  open={menuAnchor !== null}
                  onClose={closeMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  slotProps={{ paper: { sx: { minWidth: 220, mt: 1 } } }}
                >
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" noWrap>
                      {session.user.displayName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      @{session.user.username}
                    </Typography>
                  </Box>
                  <Divider />
                  <MenuItem component={RouterLink} to="/profile" onClick={closeMenu}>
                    <ListItemIcon>
                      <PersonRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('nav.profile')}</ListItemText>
                  </MenuItem>
                  {(session.user.role === 'MODERATOR' || session.user.role === 'ADMIN') && (
                    <MenuItem component={RouterLink} to="/admin" onClick={closeMenu}>
                      <ListItemIcon>
                        <GavelRoundedIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{t('nav.moderation')}</ListItemText>
                    </MenuItem>
                  )}
                  <MenuItem component={RouterLink} to="/settings" onClick={closeMenu}>
                    <ListItemIcon>
                      <SettingsRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('nav.settings')}</ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={handleSignOut} disabled={logout.isPending}>
                    <ListItemIcon>
                      <LogoutRoundedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{t('auth.signOut')}</ListItemText>
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button component={RouterLink} to="/login" size="small" variant="text">
                  {t('auth.login.submit')}
                </Button>
                <Button
                  component={RouterLink}
                  to="/register"
                  size="small"
                  variant="contained"
                  sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  {t('auth.join')}
                </Button>
              </Stack>
            )}
          </Stack>
        </Container>
      </Toolbar>
    </AppBar>
  );
}
